import React, { Component } from "react";
import { Link } from "react-router-dom";

import "./HomeBlocks.scss";
import "./HomeBlocksFull.scss";
import "./ThemeIdentities.scss";

import { isLoggedIn, joinPlan, myPlans } from "../../Helper/Api";
import { t, forward, getLanguage } from "../../Helper/i18n";
import { planPrice, membershipOf, themeLayoutOf } from "../../Helper/homeBlocks";
import Toast from "../Toast/Toast";
import HeroArt from "./HeroArt";

// The ready-made home-page blocks a theme can list (see Helper/homeBlocks.js). Colours, radii and fonts come from the
// theme's CSS variables (Helper/Theme.js); every text the teacher wrote is rendered as text, never as HTML.

function Section({ id, eyebrow, title, lead, more, children, className = "" }) {
  return (
    <section className={`hb-section ${className}`} id={id}>
      <div className="hb-wrap">
        {(title || eyebrow) && (
          <div className="hb-head">
            <div>
              {eyebrow && <span className="hb-eyebrow">{eyebrow}</span>}
              {title && <h2 className="hb-title nd-title">{title}</h2>}
              {lead && <p className="hb-lead">{lead}</p>}
            </div>
            {more && <Link className="hb-head-more" to={more.to}>{more.label} {forward()}</Link>}
          </div>
        )}
        {!title && !eyebrow && lead && <p className="hb-lead">{lead}</p>}
        {children}
      </div>
    </section>
  );
}

/** What every site on the platform really offers; shown where the teacher has written nothing of their own. */
const PERKS = () => [
  { icon: "play", title: t("hb.perk1"), text: t("hb.perk1Text") },
  { icon: "check", title: t("hb.perk2"), text: t("hb.perk2Text") },
  { icon: "live", title: t("hb.perk3"), text: t("hb.perk3Text") },
  { icon: "device", title: t("hb.perk4"), text: t("hb.perk4Text") },
];

const ICONS = {
  play: <path d="M8 5.5v13l11-6.5z" fill="currentColor" stroke="none" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  live: <><circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" /><path d="M6.3 6.3a8 8 0 0 0 0 11.4M17.7 6.3a8 8 0 0 1 0 11.4" /></>,
  device: <><rect x="7" y="3" width="10" height="18" rx="2.5" /><path d="M11 17.5h2" /></>,
  book: <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5zM4 20.5A2.5 2.5 0 0 0 6.5 23H20" />,
  chat: <path d="M4 5h16v11H9l-5 4z" />,
};

function Ico({ name }) {
  return (
    <svg className="hb-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

/** A figure the teacher wrote ("+12,000", "98%") counting up from zero the first time it is seen; text without a
 *  number, and visitors who asked for less motion, get it as written. */
class CountUp extends Component {
  state = { shown: null };
  el = React.createRef();

  componentDidMount() {
    const m = String(this.props.value).match(/^(\D*)([\d.,]+)(\D*)$/);
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!m || reduce || !("IntersectionObserver" in window)) return;
    const target = parseFloat(m[2].replace(/,/g, ""));
    if (!isFinite(target) || target <= 0) return;
    const decimals = (m[2].split(".")[1] || "").length;
    const grouped = m[2].indexOf(",") >= 0;
    const format = (n) =>
      m[1] + (grouped ? n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : n.toFixed(decimals)) + m[3];
    this.setState({ shown: format(0) });
    this.io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      this.io.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / 1400);
        this.setState({ shown: format(p < 1 ? target * (1 - Math.pow(1 - p, 3)) : target) });
        if (p < 1) this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    this.io.observe(this.el.current);
  }

  componentWillUnmount() {
    if (this.io) this.io.disconnect();
    cancelAnimationFrame(this.raf);
  }

  render() {
    const { value, as: Tag = "b" } = this.props;
    return <Tag dir="ltr" ref={this.el}>{this.state.shown === null ? value : this.state.shown}</Tag>;
  }
}

const initialsOf = (name) =>
  String(name || "").trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0)).join("").toUpperCase();

/** Per drawing (layout.art): symbols of the subject that drift around the first screen, and a band of its
 *  formulas or words that slides under it. Fixed text, never the teacher's. */
const HERO_EXTRAS = {
  graph: {
    symbols: ["∑", "π", "√x", "∫", "∞", "Δ", "x²", "θ", "≠", "÷"],
    ticker: ["f(x) = ax² + bx + c", "sin²θ + cos²θ = 1", "a² + b² = c²", "e^(iπ) + 1 = 0", "∫ x dx = x²⁄2 + C",
      "lim (1 + 1/n)ⁿ = e", "d/dx sin x = cos x", "∑ 1/2ⁿ = 1", "log(ab) = log a + log b", "(a + b)² = a² + 2ab + b²"],
  },
  orbit: {
    symbols: ["λ", "Ω", "ħ", "ν", "Δt", "∇", "μ", "ε₀", "α", "β"],
    ticker: ["E = mc²", "F = ma", "v = λf", "V = IR", "P = IV", "KE = ½mv²", "W = F·d", "p = mv", "Δx·Δp ≥ ħ⁄2", "F = G·m₁m₂⁄r²"],
  },
  molecule: {
    symbols: ["H₂O", "O₂", "CO₂", "NaCl", "CH₄", "pH", "Fe", "N₂", "He", "C"],
    ticker: ["2H₂ + O₂ → 2H₂O", "HCl + NaOH → NaCl + H₂O", "CH₄ + 2O₂ → CO₂ + 2H₂O", "PV = nRT", "pH = −log[H⁺]",
      "C₆H₁₂O₆", "N₂ + 3H₂ ⇌ 2NH₃", "CaCO₃ → CaO + CO₂"],
  },
  cell: {
    symbols: ["DNA", "RNA", "ATP", "◉", "⬡", "✿", "◌", "∞", "⚬", "❋"],
    ticker: ["الخلية", "DNA", "البناء الضوئي", "ATP", "الميتوكوندريا", "الجينات", "الإنزيمات", "الكروموسومات", "التنفس الخلوي", "الوراثة"],
  },
  ecg: {
    symbols: ["✚", "♥", "✚", "♥", "✚", "♥", "✚", "♥", "✚", "♥"],
    ticker: ["التشريح", "علم وظائف الأعضاء", "علم الأدوية", "علم الأمراض", "الكيمياء الحيوية", "التمريض", "الإسعافات الأولية", "الأحياء الدقيقة"],
  },
  blueprint: {
    symbols: ["⌀", "∠", "⊥", "△", "∥", "±", "⌒", "◫", "⊙", "≅"],
    ticker: ["σ = F⁄A", "∑F = 0", "∑M = 0", "E = σ⁄ε", "τ = T·r⁄J", "M = F·d", "P = ρgh", "Q = A·v"],
  },
  calligraphy: {
    symbols: ["ض", "ع", "ق", "ن", "ك", "ه", "ب", "م", "ل", "ص"],
    ticker: ["لغة الضاد", "نحو", "صرف", "بلاغة", "أدب", "إملاء", "تعبير", "عروض", "قراءة"],
  },
  bubbles: {
    symbols: ["A", "b", "?", "!", "C", "e", "Hi", "W", "z", "&"],
    ticker: ["Hello!", "How are you?", "Nice to meet you", "Let’s practise", "Well done!", "See you soon", "Speak up", "I can do it"],
  },
  passport: {
    symbols: ["✈", "⚑", "✉", "◎", "✈", "⚑", "✉", "◎", "✈", "⚑"],
    ticker: ["Bonjour", "Hola", "Ciao", "Hallo", "Olá", "Привет", "Merhaba", "こんにちは", "안녕하세요"],
  },
  code: {
    symbols: ["{ }", "</>", ";", "=>", "( )", "[ ]", "#", "&&", "//", "$"],
    ticker: ["const learn = () => practice()", "git commit -m \"progress\"", "SELECT * FROM skills;", "for (const day of days) code()",
      "npm run dev", "print(\"Hello, world\")", "docker compose up", "if (stuck) askTeacher()"],
  },
  shapes: {
    symbols: ["●", "▲", "■", "◆", "◐", "✶", "▱", "◯", "⬟", "✚"],
    ticker: ["لون", "تكوين", "خط", "فراغ", "تباين", "إيقاع", "توازن", "ملمس", "حركة"],
  },
  wave: {
    symbols: ["♪", "♫", "♬", "♩", "𝄞", "♭", "♯", "𝄢", "♪", "♫"],
    ticker: ["مقام راست", "مقام بياتي", "مقام نهاوند", "مقام صبا", "مقام حجاز", "Do Re Mi", "4⁄4", "Allegro", "Andante"],
  },
  map: {
    symbols: ["✦", "⚓", "⌘", "✧", "⚔", "◈", "✦", "⚓", "⌘", "✧"],
    ticker: ["3100 ق.م · توحيد القطرين", "332 ق.م · الإسكندر", "641 م · الفتح الإسلامي", "969 م · القاهرة", "1517 م · العثمانيون",
      "1805 · محمد علي", "1869 · قناة السويس", "1952 · الجمهورية"],
  },
  chart: {
    symbols: ["%", "↗", "$", "∑", "÷", "↘", "≈", "€", "↗", "£"],
    ticker: ["الإيرادات ↗", "التكاليف ↘", "الميزانية", "التدفقات النقدية", "نقطة التعادل", "هامش الربح", "قائمة الدخل", "رأس المال"],
  },
  path: {
    symbols: ["◆", "●", "▲", "◆", "●", "▲", "◆", "●", "▲", "◆"],
    ticker: ["التواصل", "إدارة الوقت", "القيادة", "العمل الجماعي", "حل المشكلات", "التفكير النقدي", "العرض والتقديم", "التفاوض"],
  },
  dumbbell: {
    symbols: ["⚡", "✕", "⚡", "✕", "⚡", "✕", "⚡", "✕", "⚡", "✕"],
    ticker: ["قوة", "تحمّل", "مرونة", "سرعة", "تغذية", "انضباط", "نوم", "استمرار"],
  },
  leaf: {
    symbols: ["❀", "✿", "❁", "✾", "❀", "✿", "❁", "✾", "❀", "✿"],
    ticker: ["نوم كويس", "مية كفاية", "أكل متوازن", "حركة يومية", "تنفّس بهدوء", "خضار وفاكهة", "راحة"],
  },
  blocks: {
    symbols: ["★", "●", "▲", "♥", "■", "✿", "★", "●", "▲", "♥"],
    ticker: ["أ ب ت ث", "1 2 3", "A B C", "ألوان", "أشكال", "حروف", "أرقام", "حكايات"],
  },
  sticker: {
    symbols: ["★", "✱", "!", "✓", "★", "✱", "!", "✓", "★", "✱"],
    ticker: ["ذاكر", "افهم", "اتمرّن", "راجع", "انجح", "كرّر", "اسأل", "ركّز"],
  },
  type: {
    symbols: [],
    ticker: ["قراءة", "تحليل", "كتابة", "نقد", "أسلوب", "حجّة", "فكرة", "سرد"],
  },
  grid3d: {
    symbols: ["✦", "◆", "✧", "◇", "✦", "◆", "✧", "◇", "✦", "◆"],
    ticker: ["LEARN", "CREATE", "LEVEL UP", "PLAY", "BUILD", "SHINE", "REPEAT"],
  },
  stack: {
    symbols: ["✦", "✧", "✦", "✧", "✦", "✧", "✦", "✧", "✦", "✧"],
  },
  polaroid: {
    symbols: ["✎", "✂", "★", "✎", "✂", "★", "✎", "✂", "★", "✎"],
  },
};

function HeroSymbols({ art }) {
  const x = HERO_EXTRAS[art];
  if (!x) return null;
  return (
    <div className="hb-symbols" aria-hidden="true">
      <b className="hb-orb o1"></b>
      <b className="hb-orb o2"></b>
      {x.symbols.map((s, i) => <span key={i} style={{ "--i": i }}>{s}</span>)}
    </div>
  );
}

function Ticker({ art }) {
  const x = HERO_EXTRAS[art];
  if (!x || !x.ticker) return null;
  const row = x.ticker.map((s, i) => <span key={i}>{s}</span>);
  return (
    <div className="hb-ticker" aria-hidden="true" dir="ltr">
      <div className="hb-ticker-track">{row}{row}</div>
    </div>
  );
}

/** The reading direction of a text by its first letter, so a headline split into words keeps its order. */
const dirOf = (text) => {
  const m = String(text || "").match(/[A-Za-z֐-ࣿ]/);
  return m && /[A-Za-z]/.test(m[0]) ? "ltr" : "rtl";
};

/** The newer themes' first screen: the teacher's own words beside the theme's drawing (or their own picture). */
function Hero({ tenant, branding, content, lessons, layout }) {
  const title = branding.heroTitle || t("heroTitle", { name: tenant.name });
  const stats = content.stats.slice(0, 2);
  const visual = branding.heroImageUrl ? (
    <div className="hb-hero-photo" style={{ backgroundImage: `url(${branding.heroImageUrl})` }}>
      {branding.tagline && <span className="hb-hero-label">{branding.tagline}</span>}
    </div>
  ) : (
    <HeroArt art={layout.art} />
  );
  // the pointer's place over the first screen, for the themes that light their background around it
  const follow = (e) => {
    const el = e.currentTarget;
    if (el.raf) return;
    const x = e.clientX;
    const y = e.clientY;
    el.raf = requestAnimationFrame(() => {
      el.raf = null;
      const r = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${x - r.left}px`);
      el.style.setProperty("--my", `${y - r.top}px`);
    });
  };
  // past the first screen: to the section that follows it
  const next = (e) => {
    const sec = e.currentTarget.closest("section");
    const after = sec && sec.nextElementSibling;
    if (after) after.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className={`hb-hero2 ${HERO_EXTRAS[layout.art] && HERO_EXTRAS[layout.art].ticker ? "has-ticker" : ""}`} onMouseMove={follow}>
      <HeroSymbols art={layout.art} />
      <div className="hb-wrap hb-hero2-in">
        <div className="hb-hero2-copy">
          {branding.heroEyebrow && <span className="hb-chip hb-chip-dot">{branding.heroEyebrow}</span>}
          {/* the headline arrives word by word; the words themselves are the teacher's, unchanged */}
          <h1 dir={dirOf(title)}>{title.split(/(\s+)/).map((w, i) => (/^\s+$/.test(w) || !w ? w : <span className="hw" key={i} style={{ "--i": i / 2 }}>{w}</span>))}</h1>
          <p>{branding.heroText || t("heroText")}</p>
          <div className="hb-hero-actions">
            <Link to="/courses" className="hb-btn hb-btn-primary hb-btn-big">{t("hb.browse")} <span aria-hidden="true">{forward()}</span></Link>
            {lessons && lessons.length > 0 ? (
              <Link to="/free-videos" className="hb-btn hb-btn-ghost hb-btn-big"><span aria-hidden="true">▶</span> {t("hb.freeLesson")}</Link>
            ) : (
              !isLoggedIn() && <Link to="/sign-up" className="hb-btn hb-btn-ghost hb-btn-big">{t("createAccount")}</Link>
            )}
          </div>
          {stats.length > 0 ? (
            <div className="hb-hero-meta">
              {stats.map((s, i) => <span key={i}><CountUp value={s.value} />{s.label}</span>)}
            </div>
          ) : (
            <ul className="hb-hero-ticks">
              {PERKS().slice(0, 3).map((p, i) => <li key={i} style={{ "--i": i }}><Ico name="check" />{p.title}</li>)}
            </ul>
          )}
        </div>
        <div className="hb-hero2-visual">
          {visual}
          {content.stats[0] && <span className="hb-float f1"><b dir="ltr">{content.stats[0].value}</b> {content.stats[0].label}</span>}
          {content.stats[1] && <span className="hb-float f2"><b dir="ltr">{content.stats[1].value}</b> {content.stats[1].label}</span>}
        </div>
      </div>
      <button type="button" className="hb-scroll" onClick={next}>
        <span className="mouse" aria-hidden="true"><i></i></span>
        {t("hb.scroll")}
      </button>
      <Ticker art={layout.art} />
    </section>
  );
}

/** The bento first screen: the teacher's words in the big box, real facts in the small ones. */
function BentoHero({ tenant, branding, content, courses, meetings }) {
  const s = content.stats;
  const course = (courses || []).find((c) => c.showOnHome) || (courses || [])[0];
  const next = (meetings || []).find((m) => new Date(m.endAt) > new Date());
  return (
    <section className="hb-hero2 is-bento">
      <div className="hb-wrap hb-bento">
        <div className="bx b-main">
          {branding.heroEyebrow && <span className="hb-chip hb-chip-dot">{branding.heroEyebrow}</span>}
          <h1>{branding.heroTitle || t("heroTitle", { name: tenant.name })}</h1>
          <p>{branding.heroText || t("heroText")}</p>
          <div className="hb-hero-actions">
            <Link to="/courses" className="hb-btn hb-btn-primary hb-btn-big">{t("hb.browse")} <span aria-hidden="true">{forward()}</span></Link>
            {!isLoggedIn() && <Link to="/sign-up" className="hb-btn hb-btn-ghost hb-btn-big">{t("createAccount")}</Link>}
          </div>
        </div>
        {s[0] ? <div className="bx b-key"><b dir="ltr">{s[0].value}</b><span>{s[0].label}</span></div>
          : <Link to="/courses" className="bx b-key"><b aria-hidden="true">▶</b><span>{t("hb.freeLesson")}</span></Link>}
        <Link to="/live" className="bx b-live">
          <span className="hb-live-dot">{t("hb.liveEyebrow")}</span>
          <b>{next ? next.title : t("hb.liveGuestTitle")}</b>
          <span>{next ? new Date(next.startAt).toLocaleString(getLanguage() === "en" ? "en-GB" : "ar-EG", { weekday: "long", hour: "numeric", minute: "2-digit" }) : t("hb.allLive")}</span>
        </Link>
        {course && (
          <a href={`/courses/${course.id}`} className="bx b-course">
            <span className="bc-th" style={course.image || course.listImage ? { backgroundImage: `url(${course.listImage || course.image})` } : undefined}>
              {!(course.image || course.listImage) && initialsOf(course.title)}
            </span>
            <b>{course.title}</b>
          </a>
        )}
        {!course && (
          <Link to="/courses" className="bx b-course">
            <span className="hb-live-dot is-plain">{t("hb.coursesEyebrow")}</span>
            <b>{t("hb.coursesTitle")}</b>
          </Link>
        )}
        {s[1] && <div className="bx b-stu"><b dir="ltr">{s[1].value}</b><span>{s[1].label}</span></div>}
        {s[2] ? <div className="bx b-acc"><b dir="ltr">{s[2].value}</b><span>{s[2].label}</span></div>
          : !s[1] && <Link to={isLoggedIn() ? "/courses" : "/sign-up"} className="bx b-acc"><b aria-hidden="true">{forward()}</b><span>{t(isLoggedIn() ? "hb.allCourses" : "hb.startFree")}</span></Link>}
      </div>
    </section>
  );
}

/** What the steps say when the teacher has written none. */
const DEFAULT_STEPS = () => [
  { title: t("hb.step1"), text: t("hb.step1Text") },
  { title: t("hb.step2"), text: t("hb.step2Text") },
  { title: t("hb.step3"), text: t("hb.step3Text") },
];

function Steps({ content }) {
  const steps = content.steps.length ? content.steps : DEFAULT_STEPS();
  return (
    <Section eyebrow={t("hb.stepsEyebrow")} title={t("hb.stepsTitle")} className="hb-steps-sec">
      <ol className="hb-steps">
        {steps.map((s, i) => (
          <li className="hb-step" key={i} style={{ "--i": i }}>
            <span className="n" aria-hidden="true">{i + 1}</span>
            <h3>{s.title}</h3>
            {s.text && <p>{s.text}</p>}
          </li>
        ))}
      </ol>
    </Section>
  );
}

/** The signed-in student's coming sessions; for a visitor, what live sessions are and the way in. */
/** A drawing of a live class: the teacher's tile, three students, a chat, and the controls. */
function LiveRoom({ teacher }) {
  return (
    <div className="hb-room" aria-hidden="true">
      <div className="rm-top">
        <span className="hb-live-dot">{t("liveNow")}</span>
        <b>{t("hb.roomTitle")}</b>
        <span className="rm-time" dir="ltr">45:12</span>
      </div>
      <div className="rm-grid">
        <span className="rm-main"><i>{initialsOf(teacher)}</i><em className="rm-wave"><s></s><s></s><s></s><s></s></em></span>
        <span className="rm-s"><i></i></span>
        <span className="rm-s"><i></i></span>
        <span className="rm-s"><i></i></span>
      </div>
      <div className="rm-chat">
        <span className="c1">{t("hb.roomChat1")}</span>
        <span className="c2">{t("hb.roomChat2")}</span>
      </div>
      <div className="rm-bar"><i></i><i></i><i className="end"></i></div>
    </div>
  );
}

function LiveSessions({ meetings, teacher }) {
  const coming = (meetings || []).filter((m) => new Date(m.endAt) > new Date()).slice(0, 3);
  const when = (d) => new Date(d);
  const lang = getLanguage() === "en" ? "en-GB" : "ar-EG";
  if (!isLoggedIn()) {
    // one wide card: the invitation beside a drawing of a live class
    return (
      <section className="hb-section hb-live-sec">
        <div className="hb-wrap">
          <div className="hb-live-guest">
            <div className="lg-copy">
              <span className="hb-eyebrow">{t("hb.liveEyebrow")}</span>
              <h2 className="hb-title nd-title">{t("hb.liveGuestTitle")}</h2>
              <p>{t("hb.liveGuestText")}</p>
              <Link to="/sign-in" className="hb-btn hb-btn-primary">{t("login")} <span aria-hidden="true">{forward()}</span></Link>
            </div>
            <LiveRoom teacher={teacher} />
          </div>
        </div>
      </section>
    );
  }
  return (
    <Section eyebrow={t("hb.liveEyebrow")} title={t("hb.liveTitle")} more={{ to: "/live", label: t("hb.allLive") }} className="hb-live-sec">
      {meetings === null ? null : coming.length === 0 ? (
        <p className="hb-muted">{t("hb.liveNone")}</p>
      ) : (
        <div className="hb-live">
          {coming.map((m) => (
            <Link to={m.actionable ? `/live/${m.id}` : "/live"} className={`hb-lv ${m.status === "LIVE" ? "is-live" : ""}`} key={m.id}>
              <span className="d"><b>{when(m.startAt).getDate()}</b>{when(m.startAt).toLocaleDateString(lang, { weekday: "short" })}</span>
              <span className="tx">
                <b>{m.title}</b>
                <span>{when(m.startAt).toLocaleTimeString(lang, { hour: "numeric", minute: "2-digit" })} · {t("minutes", { n: Math.round((new Date(m.endAt) - new Date(m.startAt)) / 60000) })}</span>
                {m.status === "LIVE" && <span className="hb-live-dot">{t("liveNow")}</span>}
              </span>
            </Link>
          ))}
        </div>
      )}
    </Section>
  );
}

function FreeLessons({ lessons }) {
  const list = (lessons || []).slice(0, 3);
  if (!list.length) return null;
  return (
    <Section eyebrow={t("hb.freeEyebrow")} title={t("hb.freeTitle")} more={{ to: "/free-videos", label: t("hb.allFree") }} className="hb-free-sec">
      <div className="hb-free">
        {list.map((l) => {
          const shot = l.thumb || l.image;
          return (
            <Link to="/free-videos" className="hb-fl" key={l.id}>
              <span className="v" style={shot ? { backgroundImage: `url(${shot})` } : undefined}><i aria-hidden="true"></i></span>
              <b>{l.lessonName}</b>
            </Link>
          );
        })}
      </div>
    </Section>
  );
}

function HonorBoard({ content }) {
  if (!content.honor.length) return null;
  return (
    <Section eyebrow={t("hb.honorEyebrow")} title={t("hb.honorTitle")} className="hb-honor-sec">
      <ol className="hb-honor">
        {content.honor.map((h, i) => (
          <li className="hb-hn" key={i} style={{ "--i": i }}>
            <span className="av" aria-hidden="true">{i === 0 ? "🏆" : initialsOf(h.name)}</span>
            <b>{h.name}</b>
            {h.score && <span className="sc" dir="ltr">{h.score}</span>}
          </li>
        ))}
      </ol>
    </Section>
  );
}

/* ---- the specialty section (layout.subject): the teacher's words, else the kind's own, beside its panel ---- */
function subjectWords(kind, content) {
  const [eyebrow, title, text] = (t("subj." + kind) || "").split("|");
  return {
    eyebrow: content.subjectEyebrow || eyebrow || "",
    title: content.subjectTitle || title || "",
    text: content.subjectText || text || "",
  };
}

const range = (n) => Array.from({ length: n }, (_, i) => i);

/** Items the teacher wrote, else a few that show what the panel is for. */
const SAMPLE_ITEMS = {
  body: [["الجهاز الدوري", "خريطة دروس وأسئلة"], ["الجهاز التنفسي", "خريطة دروس وأسئلة"], ["الجهاز العصبي", "خريطة دروس وأسئلة"]],
  tools: [["AutoCAD", "رسم ثنائي الأبعاد"], ["Revit", "نمذجة BIM"], ["ETABS", "تحليل إنشائي"]],
  clubs: [["Français", "محادثة · B1"], ["Deutsch", "محادثة · A2"], ["Español", "محادثة · A1"]],
  projects: [["متجر إلكتروني كامل", "React · Node"], ["تطبيق شات لحظي", "Socket.io"], ["داشبورد بيانات", "Python"]],
  tracks: [["تمرين الإيقاع الأول", "سهل · 70 BPM"], ["سماعي نهاوند", "متوسط · 90 BPM"], ["Canon in D", "متقدم · 110 BPM"]],
  cases: [["تحليل هامش الربح", "شركة تجزئة"], ["قائمة التدفقات النقدية", "شركة صناعية"], ["تكاليف المنتج", "مصنع أغذية"]],
  roadmap: [["اعرف نفسك", "نقاط قوتك واهتماماتك"], ["ابني الـ CV", "قالب ومراجعة"], ["اتمرّن على المقابلات", "مقابلة تجريبية"], ["قدّم واتفاوض", "على العرض"]],
  plans: [["شهري", "برنامج تمرين ومتابعة أسبوعية"], ["3 شهور", "تمرين وتغذية ومتابعة"], ["سنوي", "متابعة شخصية كاملة"]],
  meals: [["فطار", "شوفان بالفواكه"], ["غدا", "فراخ مشوية وخضار"], ["سناك", "زبادي ومكسرات"], ["عشا", "سلطة تونة"]],
  timeline: [["3100 ق.م", "توحيد القطرين"], ["641 م", "الفتح الإسلامي"], ["1805", "محمد علي"], ["1952", "الجمهورية"]],
  features: [["▶", "فيديو بجودة عالية"], ["✓", "امتحانات بتتصحح لوحدها"], ["⬇", "ملفات للتحميل"], ["●", "حصص مباشرة"], ["📈", "تقارير لولي الأمر"], ["📱", "تطبيق موبايل"]],
};

/** The week's question, answerable on the page: a right answer lights up, a wrong one shakes and shows the right
 *  one. Options come as "a / b* / c" -- the starred one is right; with no star the card only shows the options. */
class Quiz extends Component {
  state = { picked: null };

  render() {
    const { question, options, math } = this.props;
    const opts = String(options || "").split("/").map((o) => o.trim()).filter(Boolean)
      .map((o) => ({ text: o.replace(/\*$/, ""), right: o.endsWith("*") }));
    const hasAnswer = opts.some((o) => o.right);
    const { picked } = this.state;
    const done = picked !== null;
    const won = done && opts[picked] && opts[picked].right;
    return (
      <div className={`hb-panel hb-quiz ${done ? (won ? "is-won" : "is-lost") : ""}`}>
        <span className="hb-quiz-tag">{t("hb.quizTag")}</span>
        {math ? <div className="hb-eq" dir="ltr">{question}</div> : <b className="hb-q">{question}</b>}
        <div className={`hb-opts ${math ? "" : "is-col"}`} dir={math ? "ltr" : undefined}>
          {opts.map((o, i) => {
            const cls = !done ? "" : o.right ? "ok" : i === picked ? "bad" : "dim";
            return (
              <button type="button" key={i} className={cls} disabled={done || !hasAnswer} style={{ "--i": i }}
                onClick={() => this.setState({ picked: i })}>
                {o.text}
                {done && o.right && <i className="mk" aria-hidden="true">✓</i>}
                {done && i === picked && !o.right && <i className="mk" aria-hidden="true">✕</i>}
              </button>
            );
          })}
        </div>
        {hasAnswer && (
          <div className="hb-quiz-foot" aria-live="polite">
            {!done && <span>{t("hb.quizPick")}</span>}
            {done && <b>{won ? t("hb.quizRight") : t("hb.quizWrong")}</b>}
            {done && <button type="button" className="hb-quiz-again" onClick={() => this.setState({ picked: null })}>{t("hb.quizAgain")}</button>}
          </div>
        )}
        {won && <span className="hb-burst" aria-hidden="true">{range(10).map((i) => <i key={i} style={{ "--i": i }}></i>)}</span>}
      </div>
    );
  }
}

function SubjectPanel({ kind, content, plans, mine, onChanged }) {
  const items = content.subjectItems.length
    ? content.subjectItems.map((x) => [x.title || "", x.sub || ""])
    : SAMPLE_ITEMS[kind] || [];
  const list = (marker) => (
    <div className="hb-sp-list">
      {items.map(([title, sub], i) => (
        <div className="li" key={i} style={{ "--i": i }}>
          <span className="ic" aria-hidden="true">{marker === "n" ? i + 1 : marker || "•"}</span>
          <span><b>{title}</b>{sub && <span>{sub}</span>}</span>
        </div>
      ))}
    </div>
  );
  switch (kind) {
    case "problem": {
      const [eq, opts] = items[0] || ["∫₀^π sin x dx = ?", "0 / 1 / 2* / π"];
      return <Quiz question={eq} options={opts} math />;
    }
    case "mcq": {
      const [q, opts] = items[0] || ["مريض عنده ضيق تنفس مفاجئ بعد عملية، أكتر تشخيص محتمل؟", "التهاب رئوي / انسداد رئوي* / ربو حاد"];
      return <Quiz question={q} options={opts} />;
    }
    case "verse": {
      const [verse, poet] = items[0] || ["وَمَا نَيلُ المَطالِبِ بِالتَّمَنّي ... وَلَكِن تُؤخَذُ الدُنيا غِلابا", "أحمد شوقي"];
      return <div className="hb-panel hb-verse"><p>{verse}</p>{poet && <span>{poet}</span>}</div>;
    }
    case "sim":
      return (
        <div className="hb-panel">
          <svg viewBox="0 0 400 200" className="hb-sim" aria-hidden="true">
            <line x1="20" y1="180" x2="390" y2="180" stroke="var(--t-line)" strokeWidth="2" />
            <path d="M30 178 Q 200 -40 370 178" fill="none" stroke="var(--t-primary)" strokeWidth="4" strokeDasharray="2 9" strokeLinecap="round" />
            <circle className="hb-sim-ball" r="10" fill="var(--t-accent)" />
            <path d="M30 178 L80 120" stroke="var(--t-accent)" strokeWidth="4" />
          </svg>
        </div>
      );
    case "periodic":
      return (
        <div className="hb-panel">
          <div className="hb-tiles" dir="ltr">
            {[["H", 1], ["He", 2], ["Li", 3], ["Be", 4], ["B", 5], ["C", 6], ["N", 7], ["O", 8], ["F", 9], ["Ne", 10], ["Na", 11], ["Mg", 12]].map(([e, n], i) => (
              <span key={e} className={i === 5 ? "hl" : i === 7 ? "hc" : ""} style={{ "--i": i }}><small>{n}</small><b>{e}</b></span>
            ))}
          </div>
        </div>
      );
    case "levels":
      return (
        <div className="hb-panel">
          <div className="hb-ladder" dir="ltr">
            {["A1", "A2", "B1", "B2", "C1", "C2"].map((l, i) => <span key={l} style={{ height: 50 + i * 22, "--i": i }}>{l}</span>)}
          </div>
        </div>
      );
    case "juz":
      return (
        <div className="hb-panel">
          <div className="hb-juz">{range(30).map((i) => <span key={i} style={{ "--i": i }}>{i + 1}</span>)}</div>
        </div>
      );
    case "gallery":
      return <div className="hb-gal" aria-hidden="true"><i></i><i></i><i></i><i></i></div>;
    case "timeline":
      return (
        <div className="hb-panel">
          <ol className="hb-tl">{items.map(([y, e], i) => <li key={i} style={{ "--i": i }}><i aria-hidden="true"></i><b>{y}</b>{e}</li>)}</ol>
        </div>
      );
    case "journey":
      return (
        <div className="hb-panel">
          <svg viewBox="0 0 400 170" className="hb-journey" aria-hidden="true">
            <path d="M20 140 C 90 140 90 60 160 60 S 240 140 300 120 S 360 40 385 40" fill="none" stroke="var(--t-line)" strokeWidth="14" strokeLinecap="round" />
            {[[20, 140], [160, 60], [300, 120], [385, 40]].map(([x, y], i) => (
              <g key={i} className="hb-star" style={{ animationDelay: `${i * 0.3}s` }}>
                <circle cx={x} cy={y} r="17" fill="var(--t-accent)" />
                <text x={x} y={y + 6} textAnchor="middle" fontSize="16" fill="#fff">★</text>
              </g>
            ))}
          </svg>
        </div>
      );
    case "plans":
      // before the coach adds plans, an example of what the section will hold instead of a bare "no plans"
      if (plans && plans.length) return <PlanCards plans={plans} mine={mine} onChanged={onChanged} />;
      return <div className="hb-panel">{list("✓")}{!content.subjectItems.length && <span className="hb-example">{t("hb.example")}</span>}</div>;
    case "features":
      return (
        <div className="hb-fgrid">
          {items.map(([icon, title], i) => <div key={i} className={i === 0 ? "big" : ""} style={{ "--i": i }}><span className="ic" aria-hidden="true">{icon}</span><b>{title}</b></div>)}
        </div>
      );
    case "dashboard":
      return (
        <div className="hb-panel hb-dash" aria-hidden="true">
          <svg viewBox="0 0 80 80" className="ring"><circle cx="40" cy="40" r="32" fill="none" stroke="var(--t-line)" strokeWidth="9" /><circle className="hb-ring-fill" cx="40" cy="40" r="32" fill="none" stroke="var(--t-primary)" strokeWidth="9" strokeDasharray="150 201" strokeLinecap="round" transform="rotate(-90 40 40)" /></svg>
          <div className="lines"><i></i><i></i><i></i></div>
          <span className="hb-example">{t("hb.example")}</span>
        </div>
      );
    case "certificate":
      return (
        <div className="hb-cert">
          <span className="cs">{t("subj.certificate").split("|")[0]}</span>
          <i className="name" aria-hidden="true"></i>
          <i className="line" aria-hidden="true"></i>
          <span className="seal" aria-hidden="true">✓</span>
        </div>
      );
    case "calendar":
      return (
        <div className="hb-panel">
          <div className="hb-cal" aria-hidden="true">{range(14).map((i) => <span key={i} className={i === 2 || i === 9 ? "ev" : i === 6 ? "ex" : ""}>{i + 8}</span>)}</div>
        </div>
      );
    case "compare":
      return (
        <div className="hb-panel hb-cmp" aria-hidden="true">
          <div className="cbar"><i style={{ height: "46%" }}></i></div>
          <div className="cbar"><i className="up" style={{ height: "89%" }}></i></div>
          <span className="hb-example">{t("hb.example")}</span>
        </div>
      );
    default:
      return items.length ? <div className="hb-panel">{list(kind === "roadmap" ? "n" : "")}</div> : null;
  }
}

function SubjectFeature({ layout, content, plans, mine, onChanged }) {
  if (!layout.subject || layout.subject === "none") return null;
  const w = subjectWords(layout.subject, content);
  return (
    <section className={`hb-section hb-subject is-${layout.subject}`}>
      <div className="hb-wrap hb-subject-in">
        <div className="hb-subject-copy">
          {w.eyebrow && <span className="hb-eyebrow">{w.eyebrow}</span>}
          <h2 className="hb-title nd-title">{w.title}</h2>
          {w.text && <p className="hb-lead">{w.text}</p>}
          <Link to={layout.subject === "plans" ? "/account" : "/courses"} className="hb-btn hb-btn-primary">{t("hb.start")} <span aria-hidden="true">{forward()}</span></Link>
        </div>
        <SubjectPanel kind={layout.subject} content={content} plans={plans} mine={mine} onChanged={onChanged} />
      </div>
    </section>
  );
}

/** The one place a visitor becomes a member: a trial starts at once, a paid plan is asked for and the coach activates it. */
export class PlanActions extends Component {
  state = { busy: null, done: null, error: null };

  join = (trial) => {
    const { plan, onChanged } = this.props;
    if (!isLoggedIn()) {
      window.location.href = "/sign-up";
      return;
    }
    this.setState({ busy: trial ? "trial" : "paid", error: null, done: null });
    joinPlan(plan.id, trial)
      .then(() => {
        this.setState({ busy: null, done: trial ? t("trialStarted") : t("requestSent") });
        if (onChanged) onChanged();
      })
      .catch((e) => this.setState({ busy: null, error: e.message }));
  };

  render() {
    const { plan, membership } = this.props;
    const { busy, done, error } = this.state;
    if (membership) {
      return <div className="hb-plan-have">{t("alreadyHaveIt")} — {t("status" + membership.status)}</div>;
    }
    return (
      <div className="hb-plan-actions">
        {plan.trialDays > 0 && (
          <button type="button" className="hb-btn hb-btn-primary" disabled={!!busy} onClick={() => this.join(true)}>
            {busy === "trial" ? "…" : t("tryFree")}
          </button>
        )}
        <button type="button" className={`hb-btn ${plan.trialDays > 0 ? "hb-btn-ghost" : "hb-btn-primary"}`} disabled={!!busy} onClick={() => this.join(false)}>
          {busy === "paid" ? "…" : t("subscribeNow")}
        </button>
        <Toast msg={done} type="success" />
        <Toast msg={error} type="error" />
      </div>
    );
  }
}

/** Plan cards; also used by the member page. `mine` (optional) marks the plans the member already has. */
export function PlanCards({ plans, mine, onChanged }) {
  if (!plans || !plans.length) return <p className="hb-muted">{t("noPlans")}</p>;
  const lang = getLanguage();
  return (
    <div className="hb-plans">
      {plans.map((plan) => (
        <article className="hb-plan" key={plan.id}>
          <h3>{plan.name}</h3>
          <div className="hb-plan-price">{planPrice(plan, lang)}</div>
          {plan.trialDays > 0 && <div className="hb-chip">{t("trialDays", { n: plan.trialDays })}</div>}
          {plan.description && <p>{plan.description}</p>}
          <PlanActions plan={plan} membership={membershipOf(mine, plan.id)} onChanged={onChanged} />
        </article>
      ))}
    </div>
  );
}

function HeroCta({ tenant, branding, content, plans }) {
  const trialPlan = (plans || []).find((p) => p.trialDays > 0);
  const label = content.heroCtaLabel || t("startTrial");
  return (
    <section className="hb-hero">
      <div className="hb-wrap hb-hero-in">
        <div className="hb-hero-copy">
          {branding.heroEyebrow && <div className="nd-eyebrow">{branding.heroEyebrow}</div>}
          <h1>{branding.heroTitle || t("heroTitle", { name: tenant.name })}</h1>
          <p>{branding.heroText || t("heroText")}</p>
          <div className="hb-hero-actions">
            <Link to={isLoggedIn() ? "/account" : "/sign-up"} className="hb-btn hb-btn-primary hb-btn-big">
              {label} <span aria-hidden="true">{forward()}</span>
            </Link>
            <a href="#plans" className="hb-btn hb-btn-ghost hb-btn-big">{t("seePlans")}</a>
          </div>
          {trialPlan && <div className="hb-hero-note">{t("trialDays", { n: trialPlan.trialDays })}</div>}
        </div>
        <div
          className={`hb-hero-visual ${branding.heroImageUrl ? "" : "no-image"}`}
          style={branding.heroImageUrl ? { backgroundImage: `url(${branding.heroImageUrl})` } : undefined}
        >
          {branding.tagline && <div className="hb-hero-label">{branding.tagline}</div>}
        </div>
      </div>
    </section>
  );
}

function TrustStats({ content, school }) {
  if (!content.stats.length) {
    // the newer themes: what the site offers, until the teacher writes their own numbers
    if (!school) return null;
    return (
      <Section eyebrow={t("hb.perksEyebrow")} title={t("hb.perksTitle")} className="hb-perks-sec">
        <div className="hb-perks">
          {PERKS().map((p, i) => (
            <div className="hb-perk" key={i} style={{ "--i": i }}>
              <span className="ic"><Ico name={p.icon} /></span>
              <h3>{p.title}</h3>
              <p>{p.text}</p>
            </div>
          ))}
        </div>
      </Section>
    );
  }
  return (
    <Section className="hb-stats-sec">
      <div className="hb-stats">
        {content.stats.map((s, i) => (
          <div className="hb-stat" key={i} style={{ "--i": i }}>
            <CountUp value={s.value} as="strong" />
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function FeatureGrid({ content, branding }) {
  const items = content.features;
  if (!items.length && !branding.featureTitle) return null;
  return (
    <Section title={branding.featureTitle} lead={branding.featureText}>
      <div className="hb-features">
        {items.map((f, i) => (
          <div className="hb-feature" key={i}>
            {f.icon && <div className="hb-feature-icon" aria-hidden="true">{f.icon}</div>}
            <h3>{f.title}</h3>
            {f.text && <p>{f.text}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
}

function ProgramCards({ courses, school }) {
  const list = (courses || []).filter((c) => c.showOnHome);
  const shown = (list.length ? list : courses || []).slice(0, school ? 4 : 6);
  if (!shown.length) return null;
  // the newer themes call them courses and head them like every other section; the gym themes keep "programs"
  const head = school
    ? { eyebrow: t("hb.coursesEyebrow"), title: t("hb.coursesTitle"), lead: t("hb.coursesLead"), more: { to: "/courses", label: t("hb.allCourses") } }
    : { title: t("programs") };
  return (
    <Section id="programs" {...head}>
      <div className="hb-programs">
        {shown.map((c, i) => {
          const img = c.listImage || c.image;
          return (
            <a href={`/courses/${c.id}`} className="hb-program" key={c.id} style={{ "--i": i }}>
              <div className={`hb-program-img ${img ? "" : "is-blank"}`} style={img ? { backgroundImage: `url(${img})` } : undefined}>
                {!img && <span aria-hidden="true">{initialsOf(c.title)}</span>}
                {c.showOnHome && school && <em className="hb-program-tag">{t("featured")}</em>}
              </div>
              <div className="hb-program-body">
                <h3>{c.title}</h3>
                {school && c.description && <p>{c.description}</p>}
                <div className="hb-program-meta">
                  {c.level && <span className="hb-chip">{c.level}</span>}
                  {c.instructorName && <span>{c.instructorName}</span>}
                </div>
              </div>
            </a>
          );
        })}
      </div>
      {!school && <p className="hb-more"><Link to="/courses">{t("seeAllPrograms")} {forward()}</Link></p>}
    </Section>
  );
}

function PricingPlans({ plans, mine, onChanged }) {
  return (
    <Section id="plans" title={t("ourPlans")} lead={t("plansLead")}>
      <PlanCards plans={plans} mine={mine} onChanged={onChanged} />
      <p className="hb-muted hb-pay-note">{t("payNote")}</p>
    </Section>
  );
}

function Person({ p, big }) {
  return (
    <article className={`hb-person ${big ? "big" : ""}`}>
      <div className="hb-person-img" style={p.imageUrl ? { backgroundImage: `url(${p.imageUrl})` } : undefined}>
        {!p.imageUrl && <span aria-hidden="true">{(p.name || "?").trim().charAt(0)}</span>}
      </div>
      <h3>{p.name}</h3>
      {p.specialty && <div className="hb-person-role">{p.specialty}</div>}
      {p.bio && <p>{p.bio}</p>}
    </article>
  );
}

function TeamGrid({ instructors, tenant }) {
  const list = instructors || [];
  if (list.length <= 1) return <CoachBio instructors={list} tenant={tenant} />;
  return (
    <Section id="team" title={t("ourTeam")}>
      <div className="hb-team">{list.map((p) => <Person p={p} key={p.id} />)}</div>
    </Section>
  );
}

function CoachBio({ instructors, tenant, school, content, art }) {
  const own = (instructors || [])[0];
  const person = own || (tenant.description ? { name: tenant.name, bio: tenant.description, imageUrl: tenant.image } : null);
  if (!person) return null;
  if (!school) {
    return (
      <Section id="coach" title={t("meetCoach")}>
        <Person p={person} big />
      </Section>
    );
  }
  // the newer themes: the teacher's picture beside their words, with their own numbers under them
  return (
    <section className="hb-section hb-bio-sec" id="coach">
      <div className="hb-wrap hb-bio">
        {/* the picture, with the subject's own symbols floating around it (the theme's drawing set, see HERO_EXTRAS) */}
        <div className="hb-portrait-wrap">
          <div className="hb-portrait" style={person.imageUrl ? { backgroundImage: `url(${person.imageUrl})` } : undefined}>
            {!person.imageUrl && <span aria-hidden="true">{initialsOf(person.name)}</span>}
          </div>
          {HERO_EXTRAS[art] && HERO_EXTRAS[art].symbols.slice(0, 3).map((s, i) => (
            <span key={i} className={`hb-portrait-sym s${i + 1}`} aria-hidden="true">{s}</span>
          ))}
        </div>
        <div className="hb-bio-copy">
          <span className="hb-eyebrow">{t("hb.aboutEyebrow")}</span>
          <h2 className="hb-title nd-title">{person.name}</h2>
          {person.specialty && <div className="hb-person-role">{person.specialty}</div>}
          {person.bio && <p className="hb-lead">{person.bio}</p>}
          {content.stats.length > 0 ? (
            <div className="hb-creds">
              {content.stats.slice(0, 3).map((s, i) => <div key={i}><CountUp value={s.value} />{s.label}</div>)}
            </div>
          ) : (
            <div className="hb-bio-links">
              <Link to="/courses"><Ico name="book" />{t("hb.bioCourses")}</Link>
              <Link to="/live"><Ico name="live" />{t("hb.bioLive")}</Link>
              <Link to="/contact"><Ico name="chat" />{t("hb.bioContact")}</Link>
            </div>
          )}
          <Link to="/about" className="hb-btn hb-btn-ghost">{t("aboutName", { name: person.name })} <span aria-hidden="true">{forward()}</span></Link>
        </div>
      </div>
    </section>
  );
}

function Testimonials({ content, school }) {
  if (!content.testimonials.length) return null;
  return (
    <Section eyebrow={school ? t("hb.quotesEyebrow") : null} title={t("whatMembersSay")}>
      <div className="hb-quotes">
        {content.testimonials.map((q, i) => (
          <blockquote className="hb-quote" key={i} style={{ "--i": i }}>
            {school && <span className="hb-stars" aria-hidden="true">★★★★★</span>}
            <p>{q.text}</p>
            <footer>
              {school && <i aria-hidden="true">{initialsOf(q.name)}</i>}
              <span>
                <b>{q.name}</b>
                {q.role && <span>{q.role}</span>}
              </span>
            </footer>
          </blockquote>
        ))}
      </div>
    </Section>
  );
}

/** The site's own answers, shown by the newer themes until the teacher writes theirs. */
const DEFAULT_FAQ = () => [1, 2, 3, 4].map((n) => ({ q: t("hb.faq" + n), a: t("hb.faq" + n + "a") }));

function Faq({ content, school }) {
  const faq = content.faq.length ? content.faq : school ? DEFAULT_FAQ() : [];
  if (!faq.length) return null;
  const list = (
    <div className="hb-faq">
      {faq.map((f, i) => (
        <details key={i} open={school && i === 0}>
          <summary>{f.q}</summary>
          <p>{f.a}</p>
        </details>
      ))}
    </div>
  );
  if (!school) return <Section title={t("faqTitle")}>{list}</Section>;
  return (
    <section className="hb-section hb-faq-sec">
      <div className="hb-wrap hb-faq-split">
        <div className="hb-head">
          <div>
            <span className="hb-eyebrow">{t("hb.faqEyebrow")}</span>
            <h2 className="hb-title nd-title">{t("faqTitle")}</h2>
            <p className="hb-lead">{t("hb.faqLead")}</p>
            {/* a question that is not on the list: the way to ask it */}
            <div className="hb-faq-ask">
              <span className="ic" aria-hidden="true"><Ico name="chat" /></span>
              <span>
                <b>{t("hb.askTitle")}</b>
                <small>{t("hb.askText")}</small>
              </span>
              <Link to="/contact" className="hb-btn hb-btn-ghost">{t("contact")} <span aria-hidden="true">{forward()}</span></Link>
            </div>
          </div>
        </div>
        {list}
      </div>
    </section>
  );
}

function AppBadges({ content }) {
  const { ios, android } = content.apps;
  if (!ios && !android) return null;
  return (
    <Section title={t("getTheApp")} className="hb-apps-sec">
      <div className="hb-apps">
        {ios && <a className="hb-store" href={ios} target="_blank" rel="noopener noreferrer"><i className="fab fa-apple" aria-hidden="true" /> {t("appStore")}</a>}
        {android && <a className="hb-store" href={android} target="_blank" rel="noopener noreferrer"><i className="fab fa-google-play" aria-hidden="true" /> {t("googlePlay")}</a>}
      </div>
    </Section>
  );
}

function CtaBand({ content, plans, school, tenant }) {
  const trialPlan = (plans || []).find((p) => p.trialDays > 0);
  if (school) {
    const { ios, android } = content.apps;
    return (
      <section className="hb-section hb-cta2-sec">
        <div className="hb-wrap">
          <div className="hb-cta2">
            <div>
              <h2>{content.ctaTitle || t("hb.ctaTitle", { name: tenant.name })}</h2>
              <p>{content.ctaText || t("hb.ctaText")}</p>
              {(ios || android) && (
                <div className="hb-cta2-apps">
                  {ios && <a href={ios} target="_blank" rel="noopener noreferrer"><i className="fab fa-apple" aria-hidden="true" /> {t("appStore")}</a>}
                  {android && <a href={android} target="_blank" rel="noopener noreferrer"><i className="fab fa-google-play" aria-hidden="true" /> {t("googlePlay")}</a>}
                </div>
              )}
            </div>
            <Link to={isLoggedIn() ? "/courses" : "/sign-up"} className="hb-btn hb-btn-big hb-btn-accent">
              {content.heroCtaLabel || t("hb.startFree")} <span aria-hidden="true">{forward()}</span>
            </Link>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="hb-cta">
      <div className="hb-wrap hb-cta-in">
        <div>
          <h2>{content.ctaTitle || t("startTrial")}</h2>
          {(content.ctaText || trialPlan) && <p>{content.ctaText || t("trialDays", { n: trialPlan.trialDays })}</p>}
        </div>
        <Link to={isLoggedIn() ? "/account" : "/sign-up"} className="hb-btn hb-btn-light hb-btn-big">
          {content.heroCtaLabel || t("startTrial")} <span aria-hidden="true">{forward()}</span>
        </Link>
      </div>
    </section>
  );
}

/** Renders the theme's blocks in order. */
export default class HomeBlocks extends Component {
  state = { mine: [] };
  root = React.createRef();

  componentDidMount() {
    this.loadMine();
    this.reveal();
  }

  componentDidUpdate() {
    this.reveal();
  }

  componentWillUnmount() {
    if (this.io) this.io.disconnect();
  }

  // Each section of a newer theme rises in, and starts its own animations, the first time it scrolls into view.
  // Sections that arrive later (courses, sessions) are picked up on the next update.
  reveal = () => {
    const root = this.root.current;
    if (!root || !root.classList.contains("is-full")) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) return;
    if (!this.io) {
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add("is-in");
          this.io.unobserve(e.target);
        });
      }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
      root.classList.add("can-reveal");
    }
    Array.from(root.children).forEach((el) => {
      if (el.tagName !== "SECTION" || el.hasAttribute("data-rv")) return;
      el.setAttribute("data-rv", "");
      this.io.observe(el);
    });
  };

  loadMine = () => {
    if (!isLoggedIn()) return;
    myPlans().then((mine) => this.setState({ mine: mine || [] })).catch(() => {});
  };

  render() {
    const { blocks, tenant, branding, content, courses, plans, instructors, lessons, meetings } = this.props;
    const { mine } = this.state;
    const layout = themeLayoutOf(branding);
    // a theme with its own first screen is one of the newer, full-page themes: its blocks take the fuller look
    const school = blocks.indexOf("hero") >= 0;
    return (
      <div className={`hb-page ${school ? "is-full" : ""}`} ref={this.root}>
        {blocks.map((name) => {
          switch (name) {
            case "heroCta": return <HeroCta key={name} tenant={tenant} branding={branding} content={content} plans={plans} />;
            case "hero":
              return layout.hero === "bento"
                ? <BentoHero key={name} tenant={tenant} branding={branding} content={content} courses={courses} meetings={meetings} />
                : <Hero key={name} tenant={tenant} branding={branding} content={content} lessons={lessons} layout={layout} />;
            case "trustStats": return <TrustStats key={name} content={content} school={school} />;
            case "featureGrid": return <FeatureGrid key={name} content={content} branding={branding} />;
            case "programCards": return <ProgramCards key={name} courses={courses} school={school} />;
            case "subjectFeature": return <SubjectFeature key={name} layout={layout} content={content} plans={plans} mine={mine} onChanged={this.loadMine} />;
            case "steps": return <Steps key={name} content={content} />;
            case "pricingPlans": return <PricingPlans key={name} plans={plans} mine={mine} onChanged={this.loadMine} />;
            case "teamGrid": return <TeamGrid key={name} instructors={instructors} tenant={tenant} />;
            case "coachBio": return <CoachBio key={name} instructors={instructors} tenant={tenant} school={school} content={content} art={layout.art} />;
            case "liveSessions": return <LiveSessions key={name} meetings={meetings} teacher={tenant.name} />;
            case "freeLessons": return <FreeLessons key={name} lessons={lessons} />;
            case "honorBoard": return <HonorBoard key={name} content={content} />;
            case "testimonials": return <Testimonials key={name} content={content} school={school} />;
            case "faq": return <Faq key={name} content={content} school={school} />;
            case "appBadges": return <AppBadges key={name} content={content} />;
            case "ctaBand": return <CtaBand key={name} content={content} plans={plans} school={school} tenant={tenant} />;
            default: return null;
          }
        })}
      </div>
    );
  }
}
